/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { checkSession, logout as logoutThunk } from "../store/thunks/authThunks";
import { Skeleton } from "../components/Skeleton";
import api from "../services/api";

const AuthContext = createContext(null);

const LOCATION_INTERVAL = 10 * 60 * 1000; // 10 minutes

function sendLocation() {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      api.put("/api/auth/location", {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      }).catch(() => {});
    },
    () => {},
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const { user, isAuthenticated, isAdmin, isSuperAdmin, loading } = useSelector((s) => s.auth);
  const intervalRef = useRef(null);

  // Verify session on mount by calling /me (cookie sent automatically)
  useEffect(() => {
    dispatch(checkSession());
  }, [dispatch]);

  // Periodic location updates while authenticated
  useEffect(() => {
    if (isAuthenticated) {
      sendLocation(); // send immediately on session restore
      intervalRef.current = setInterval(sendLocation, LOCATION_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated]);

  const login = useCallback(() => {
    // No-op — verifyOtp thunk already sets Redux state
  }, []);

  const logoutFn = useCallback(() => {
    dispatch(logoutThunk());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Top nav skeleton */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        {/* Content area skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-72 mb-8" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, isSuperAdmin, login, logout: logoutFn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
